import { ApiError, isNetworkError } from "@ronin/api-client";
import { Button, Card, CardDescription, CardHeader, CardTitle, Input, Label } from "@ronin/ui";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { BrandMark } from "./brand.js";

export interface AuthField {
  name: string;
  label: string;
  type: "text" | "email" | "password" | "url";
  autoComplete: string;
  hint?: string;
}

interface AuthFormProps {
  title: string;
  description: ReactNode;
  fields: AuthField[];
  submitLabel: string;
  onSubmit: (values: Record<string, string>) => Promise<unknown>;
  /** Below the form, e.g. the server address and "Change server" in the app (spec08). */
  footer?: ReactNode;
}

/** Shared card form for sign-in and first-user setup. Validation happens in the API contract. */
export function AuthForm({
  title,
  description,
  fields,
  submitLabel,
  onSubmit,
  footer,
}: AuthFormProps) {
  const id = useId();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const values = Object.fromEntries(fields.map((f) => [f.name, String(data.get(f.name) ?? "")]));
    setPending(true);
    setError(undefined);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(
        isNetworkError(err)
          ? "No connection. Check your network and try again."
          : err instanceof ApiError
            ? err.message
            : "Something went wrong. Please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-svh flex-col items-center justify-center gap-6 px-4 py-[max(2.5rem,env(safe-area-inset-top))]">
      <BrandMark />
      <Card className="w-full max-w-sm gap-5 p-6">
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {fields.map((field) => (
            <div key={field.name} className="flex flex-col gap-1">
              <Label htmlFor={`${id}-${field.name}`}>{field.label}</Label>
              <Input
                id={`${id}-${field.name}`}
                name={field.name}
                type={field.type}
                autoComplete={field.autoComplete}
                aria-describedby={field.hint ? `${id}-${field.name}-hint` : undefined}
                required
              />
              {field.hint && (
                <p id={`${id}-${field.name}-hint`} className="text-[11px] text-faint-foreground">
                  {field.hint}
                </p>
              )}
            </div>
          ))}
          {error && (
            <p
              role="alert"
              className="border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </p>
          )}
          <Button type="submit" className="mt-1 w-full" disabled={pending}>
            {pending ? "Please wait…" : submitLabel}
          </Button>
        </form>
        {footer}
      </Card>
    </main>
  );
}
